import { apiRequest } from "../api/client";

export interface UploadedAsset {
  id: string;
  url: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  width?: number | null;
  height?: number | null;
  duration_seconds?: number | null;
  asset_type: string;
  created_at?: string;
}

export interface PreparedUpload {
  file: File;
  optimized: boolean;
  originalSize: number;
  outputSize: number;
  width?: number;
  height?: number;
  durationSeconds?: number;
}

export const SERVER_IMAGE_LIMIT_BYTES = 12 * 1024 * 1024;
export const TARGET_IMAGE_BYTES = 9.5 * 1024 * 1024;
export const MAX_SOURCE_IMAGE_BYTES = 100 * 1024 * 1024;
export const MAX_IMAGE_DIMENSION = 4096;
export const MAX_VIDEO_DURATION_SECONDS = 30;
export const SERVER_VIDEO_LIMIT_BYTES = 18 * 1024 * 1024;
export const TARGET_VIDEO_BYTES = 14 * 1024 * 1024;
export const MAX_SOURCE_VIDEO_BYTES = 500 * 1024 * 1024;
export const MAX_VIDEO_DIMENSION = 1280;

const serverImageTypes = new Set(["image/jpeg", "image/png", "image/webp", "image/avif", "image/gif"]);
const convertibleImageTypes = new Set(["image/jpeg", "image/png", "image/webp", "image/avif", "image/heic", "image/heif"]);
const imageExtensions = new Set(["jpg", "jpeg", "png", "webp", "avif", "gif", "heic", "heif"]);
const serverVideoTypes = new Set(["video/mp4", "video/webm"]);
const videoExtensions = new Set(["mp4", "m4v", "mov", "webm"]);

function extensionOf(fileName: string) {
  return fileName.split(".").pop()?.toLowerCase() || "";
}

function normalizedImageType(file: Pick<File, "name" | "type">) {
  const mimeType = file.type.toLowerCase();
  if (mimeType) return mimeType;
  const extension = extensionOf(file.name);
  if (extension === "jpg" || extension === "jpeg") return "image/jpeg";
  if (extension === "heic" || extension === "heif") return `image/${extension}`;
  return extension ? `image/${extension}` : "";
}

export function isImageUpload(file: Pick<File, "name" | "type">) {
  return file.type.toLowerCase().startsWith("image/") || imageExtensions.has(extensionOf(file.name));
}

function normalizedVideoType(file: Pick<File, "name" | "type">) {
  const mimeType = file.type.toLowerCase().split(";", 1)[0];
  if (mimeType.startsWith("video/")) return mimeType;
  const extension = extensionOf(file.name);
  if (extension === "mp4" || extension === "m4v") return "video/mp4";
  if (extension === "webm") return "video/webm";
  if (extension === "mov") return "video/quicktime";
  return "";
}

export function isVideoUpload(file: Pick<File, "name" | "type">) {
  const mimeType = file.type.toLowerCase();
  if (mimeType.startsWith("audio/")) return false;
  return mimeType.startsWith("video/") || (!mimeType && videoExtensions.has(extensionOf(file.name)));
}

export function imageNeedsOptimization(file: Pick<File, "name" | "type" | "size">) {
  if (!isImageUpload(file)) return false;
  const mimeType = normalizedImageType(file);
  return !serverImageTypes.has(mimeType) || file.size > SERVER_IMAGE_LIMIT_BYTES;
}

export function scaledImageDimensions(width: number, height: number, maximum = MAX_IMAGE_DIMENSION) {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new Error("Image dimensions are invalid.");
  }
  const scale = Math.min(1, maximum / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  };
}

export function scaledVideoDimensions(width: number, height: number, maximum = MAX_VIDEO_DIMENSION) {
  const scaled = scaledImageDimensions(width, height, maximum);
  return {
    width: Math.max(2, Math.round(scaled.width / 2) * 2),
    height: Math.max(2, Math.round(scaled.height / 2) * 2)
  };
}

export function formatUploadSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function optimizedFileName(originalName: string, mimeType: string) {
  const base = originalName.replace(/\.[^.]+$/, "").trim() || "image";
  const extension = mimeType === "image/webp" ? "webp" : mimeType === "image/png" ? "png" : "jpg";
  return `${base}-optimized.${extension}`;
}

type DecodedImage = {
  source: CanvasImageSource;
  width: number;
  height: number;
  dispose: () => void;
};

async function decodeImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    try {
      let bitmap: ImageBitmap;
      try {
        bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      } catch {
        bitmap = await createImageBitmap(file);
      }
      return { source: bitmap, width: bitmap.width, height: bitmap.height, dispose: () => bitmap.close() };
    } catch {
      // Fall through to the HTML image decoder for browsers with partial bitmap support.
    }
  }

  if (typeof Image === "undefined" || typeof URL.createObjectURL !== "function") {
    throw new Error("This browser cannot decode the selected image.");
  }

  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.decoding = "async";
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ source: image, width: image.naturalWidth, height: image.naturalHeight, dispose: () => undefined });
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("The selected image cannot be decoded. HEIC support depends on the browser and device."));
    };
    image.src = objectUrl;
  });
}

function renderCanvas(source: CanvasImageSource, width: number, height: number, opaque: boolean) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { alpha: !opaque });
  if (!context) throw new Error("Image optimization is not available in this browser.");
  if (opaque) {
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
  }
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(source, 0, 0, width, height);
  return canvas;
}

function encodeCanvas(canvas: HTMLCanvasElement, mimeType: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("The browser could not encode the optimized image.")), mimeType, quality);
  });
}

async function compressImage(file: File, onStatus?: (status: string) => void): Promise<PreparedUpload> {
  onStatus?.(`Optimizing ${formatUploadSize(file.size)} image…`);
  const decoded = await decodeImage(file);
  let dimensions = scaledImageDimensions(decoded.width, decoded.height);
  let bestBlob: Blob | null = null;
  let bestDimensions = dimensions;

  try {
    let outputType = "image/webp";
    const probeCanvas = renderCanvas(decoded.source, Math.min(dimensions.width, 64), Math.min(dimensions.height, 64), false);
    const probe = await encodeCanvas(probeCanvas, outputType, 0.92);
    probeCanvas.width = 0;
    probeCanvas.height = 0;
    if (probe.type !== outputType) outputType = normalizedImageType(file) === "image/png" ? "image/png" : "image/jpeg";

    const qualities = outputType === "image/png" ? [undefined] : [0.92, 0.88, 0.84, 0.8, 0.74, 0.68];
    for (let resizeAttempt = 0; resizeAttempt < 5; resizeAttempt += 1) {
      const canvas = renderCanvas(decoded.source, dimensions.width, dimensions.height, outputType === "image/jpeg");
      for (const quality of qualities) {
        const blob = await encodeCanvas(canvas, outputType, quality);
        if (blob.type !== outputType) continue;
        if (!bestBlob || blob.size < bestBlob.size) {
          bestBlob = blob;
          bestDimensions = dimensions;
        }
        if (blob.size <= TARGET_IMAGE_BYTES) {
          const optimized = new File([blob], optimizedFileName(file.name, outputType), { type: outputType, lastModified: file.lastModified });
          canvas.width = 0;
          canvas.height = 0;
          return { file: optimized, optimized: true, originalSize: file.size, outputSize: optimized.size, ...dimensions };
        }
      }
      canvas.width = 0;
      canvas.height = 0;
      dimensions = scaledImageDimensions(dimensions.width, dimensions.height, Math.max(1280, Math.round(Math.max(dimensions.width, dimensions.height) * 0.82)));
    }

    if (bestBlob && bestBlob.size <= SERVER_IMAGE_LIMIT_BYTES) {
      const optimized = new File([bestBlob], optimizedFileName(file.name, bestBlob.type), { type: bestBlob.type, lastModified: file.lastModified });
      return { file: optimized, optimized: true, originalSize: file.size, outputSize: optimized.size, ...bestDimensions };
    }
    throw new Error("The image could not be reduced below 12 MB without making it too small. Try exporting it as JPG or WebP first.");
  } finally {
    decoded.dispose();
  }
}

type DecodedVideo = {
  element: HTMLVideoElement;
  duration: number;
  width: number;
  height: number;
  dispose: () => void;
};

function decodeVideo(file: File): Promise<DecodedVideo> {
  if (typeof document === "undefined" || typeof URL.createObjectURL !== "function") {
    return Promise.reject(new Error("This browser cannot prepare background videos."));
  }
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    const dispose = () => {
      video.pause();
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(objectUrl);
    };
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0 || !video.videoWidth || !video.videoHeight) {
        dispose();
        reject(new Error("The video duration or dimensions could not be read."));
        return;
      }
      resolve({ element: video, duration: video.duration, width: video.videoWidth, height: video.videoHeight, dispose });
    };
    video.onerror = () => {
      dispose();
      reject(new Error("This video cannot be decoded by the browser. Try an MP4 (H.264) or WebM file."));
    };
    video.src = objectUrl;
    video.load();
  });
}

function preferredRecordingType() {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") return "";
  const candidates = [
    "video/mp4;codecs=avc1.42E01E",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm"
  ];
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) || "";
}

function optimizedVideoFileName(originalName: string, mimeType: string) {
  const base = originalName.replace(/\.[^.]+$/, "").trim() || "background-video";
  return `${base}-compressed-30s.${mimeType === "video/mp4" ? "mp4" : "webm"}`;
}

async function compressVideo(file: File, decoded: DecodedVideo, onStatus?: (status: string) => void): Promise<PreparedUpload> {
  const sourceType = normalizedVideoType(file);
  const compatibleSource = file.type.toLowerCase().split(";", 1)[0] === sourceType
    ? file
    : new File([file], file.name, { type: sourceType, lastModified: file.lastModified });
  const targetDuration = Math.min(decoded.duration, MAX_VIDEO_DURATION_SECONDS);
  if (serverVideoTypes.has(sourceType) && file.size <= TARGET_VIDEO_BYTES && decoded.duration <= MAX_VIDEO_DURATION_SECONDS) {
    return { file: compatibleSource, optimized: false, originalSize: file.size, outputSize: file.size, width: decoded.width, height: decoded.height, durationSeconds: targetDuration };
  }

  const canvas = document.createElement("canvas");
  const dimensions = scaledVideoDimensions(decoded.width, decoded.height);
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;
  const context = canvas.getContext("2d", { alpha: false });
  const recordingType = preferredRecordingType();
  if (!context || typeof canvas.captureStream !== "function" || !recordingType) {
    if (serverVideoTypes.has(sourceType) && file.size <= SERVER_VIDEO_LIMIT_BYTES && decoded.duration <= MAX_VIDEO_DURATION_SECONDS) {
      return { file: compatibleSource, optimized: false, originalSize: file.size, outputSize: file.size, width: decoded.width, height: decoded.height, durationSeconds: targetDuration };
    }
    throw new Error("This browser cannot compress the selected video. Use current Chrome, Edge, Firefox, or Safari and try again.");
  }

  onStatus?.(decoded.duration > MAX_VIDEO_DURATION_SECONDS
    ? `Trimming to ${MAX_VIDEO_DURATION_SECONDS} seconds and compressing for web playback...`
    : `Compressing ${formatUploadSize(file.size)} video for web playback...`);
  const stream = canvas.captureStream(24);
  const recorder = new MediaRecorder(stream, { mimeType: recordingType, videoBitsPerSecond: 2_200_000 });
  const chunks: BlobPart[] = [];
  const recording = new Promise<Blob>((resolve, reject) => {
    recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
    recorder.onerror = () => reject(new Error("The browser video encoder stopped unexpectedly."));
    recorder.onstop = () => resolve(new Blob(chunks, { type: recorder.mimeType || recordingType }));
  });

  recorder.start(1000);
  const startedAt = performance.now();
  let lastReportedSecond = -1;
  let captureError: unknown;
  try {
    decoded.element.currentTime = 0;
    await decoded.element.play();
    await new Promise<void>((resolve) => {
      const drawFrame = () => {
        context.drawImage(decoded.element, 0, 0, dimensions.width, dimensions.height);
        const elapsedSeconds = (performance.now() - startedAt) / 1000;
        const progressSecond = Math.min(Math.floor(decoded.element.currentTime), Math.floor(targetDuration));
        if (progressSecond !== lastReportedSecond) {
          lastReportedSecond = progressSecond;
          onStatus?.(`Compressing video ${Math.min(progressSecond + 1, Math.ceil(targetDuration))}/${Math.ceil(targetDuration)} seconds...`);
        }
        if (decoded.element.ended || decoded.element.currentTime >= targetDuration || elapsedSeconds >= targetDuration) {
          resolve();
          return;
        }
        requestAnimationFrame(drawFrame);
      };
      drawFrame();
    });
  } catch (caught) {
    captureError = caught;
  } finally {
    decoded.element.pause();
    if (recorder.state !== "inactive") recorder.stop();
  }

  let blob: Blob;
  try {
    blob = await recording;
  } finally {
    stream.getTracks().forEach((track) => track.stop());
    canvas.width = 0;
    canvas.height = 0;
  }
  if (captureError) throw captureError;
  const outputType = (blob.type || recorder.mimeType || recordingType).toLowerCase().split(";", 1)[0];
  if (!serverVideoTypes.has(outputType)) throw new Error("The browser produced an unsupported video format. Try MP4 or WebM.");
  if (!blob.size || blob.size > SERVER_VIDEO_LIMIT_BYTES) throw new Error("The compressed video is still too large. Try a shorter clip or a lower-resolution source.");
  const optimized = new File([blob], optimizedVideoFileName(file.name, outputType), { type: outputType, lastModified: file.lastModified });
  return { file: optimized, optimized: true, originalSize: file.size, outputSize: optimized.size, ...dimensions, durationSeconds: targetDuration };
}

async function prepareVideo(file: File, onStatus?: (status: string) => void): Promise<PreparedUpload> {
  if (file.size > MAX_SOURCE_VIDEO_BYTES) throw new Error("The original video must be smaller than 500 MB for safe browser compression.");
  onStatus?.("Reading video duration and dimensions...");
  const decoded = await decodeVideo(file);
  try {
    return await compressVideo(file, decoded, onStatus);
  } finally {
    decoded.dispose();
  }
}

export async function prepareUploadFile(file: File, onStatus?: (status: string) => void): Promise<PreparedUpload> {
  if (!file.size) throw new Error("The selected file is empty.");
  if (isVideoUpload(file)) return prepareVideo(file, onStatus);
  if (!isImageUpload(file)) return { file, optimized: false, originalSize: file.size, outputSize: file.size };

  const mimeType = normalizedImageType(file);
  if (mimeType === "image/gif" && file.size > SERVER_IMAGE_LIMIT_BYTES) {
    throw new Error("Animated GIF files over 12 MB are not compressed automatically because that would remove the animation. Use a smaller GIF or convert it to video.");
  }
  if (!serverImageTypes.has(mimeType) && !convertibleImageTypes.has(mimeType)) {
    throw new Error("Use JPG, PNG, WebP, AVIF, HEIC, or HEIF for image uploads.");
  }
  if (file.size > MAX_SOURCE_IMAGE_BYTES) {
    throw new Error("The original image must be smaller than 100 MB for safe browser optimization.");
  }
  if (!imageNeedsOptimization(file)) return { file, optimized: false, originalSize: file.size, outputSize: file.size };
  return compressImage(file, onStatus);
}

export async function uploadProjectAsset(projectId: string, assetType: string, originalFile: File, onStatus?: (status: string) => void) {
  const prepared = await prepareUploadFile(originalFile, onStatus);
  onStatus?.(prepared.optimized
    ? `Optimized ${formatUploadSize(prepared.originalSize)} → ${formatUploadSize(prepared.outputSize)}. Uploading…`
    : "Uploading…");
  const form = new FormData();
  form.set("file", prepared.file);
  form.set("assetType", assetType);
  if (prepared.durationSeconds) form.set("durationSeconds", String(prepared.durationSeconds));
  const response = await apiRequest<{ ok: true; asset: UploadedAsset }>(`/api/projects/${projectId}/assets`, { method: "POST", body: form });
  return { asset: response.asset, prepared };
}
